/**
 * Your own name, title, phone and employer.
 *
 * Name and title leave the building in every outreach template — a rep whose
 * record still says their email address sends mail signed with their email
 * address. Phone and company leave it a different way: they are what the
 * digital business card hands to a stranger, and the QR code is built from
 * them, so the card is only as current as this form.
 *
 * The photo has two sources and a crop step between them and the bucket. A
 * centre-crop alone is right often enough to look like a good default and wrong
 * often enough to be irritating — someone standing at the edge of a group shot
 * gets cropped to a shoulder — so `PhotoEditor` sits in the middle of both
 * routes rather than only the camera one.
 */
import { useRef, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { useAuth } from './AuthProvider'
import { BusinessCard } from './BusinessCard'
import { ACCEPTED_IMAGE_TYPES, removeProfilePhoto, uploadProfilePhoto } from './photo'
import { PhotoEditor } from './PhotoEditor'
import { CameraCapture } from './CameraCapture'
import { useIsMobile } from '@/shared/nav/useIsMobile'

export function ProfileModal({ onClose }: { onClose: () => void }) {
  const { user, saveProfile } = useAuth()
  const [name, setName] = useState(user?.displayName ?? '')
  const [title, setTitle] = useState(user?.title ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')
  const [company, setCompany] = useState(user?.company ?? 'Skida')
  const [photoUrl, setPhotoUrl] = useState(user?.photoUrl ?? '')
  const [uploading, setUploading] = useState(false)
  // The picked or captured image, waiting to be cropped.
  const [pending, setPending] = useState<File | null>(null)
  const [camera, setCamera] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()
  const [saving, setSaving] = useState(false)
  const [showCard, setShowCard] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!user) return null

  const save = async () => {
    if (!name.trim()) {
      setError('A name is needed — it is what outreach mail is signed with.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await saveProfile(name, title, phone, company, photoUrl)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const store = async (blob: Blob) => {
    if (!user) return
    setPending(null)
    setUploading(true)
    setError(null)
    try {
      setPhotoUrl(await uploadProfilePhoto(user.email, blob))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the photo.')
    } finally {
      setUploading(false)
    }
  }

  const dropPhoto = async () => {
    if (!user) return
    setUploading(true)
    try {
      await removeProfilePhoto(user.email)
      setPhotoUrl('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the photo.')
    } finally {
      setUploading(false)
    }
  }

  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('') || '?'

  return (
    <Modal
      title="Edit profile"
      onClose={onClose}
      width={420}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="recform">
        <div className="photopick">
          {photoUrl ? (
            <img className="photopick__img" src={photoUrl} alt="" />
          ) : (
            <div className="photopick__img photopick__img--initials">{initials}</div>
          )}
          <div className="photopick__actions">
            {/* Cleared on every pick so choosing the same file twice still
                fires a change event — otherwise a cancelled crop cannot be
                retried with the same photo. */}
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              className="photopick__input"
              onChange={(e) => {
                setPending(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
            {/* `capture` hands a phone straight to its own camera app, which
                takes a far better picture than a video frame and is the
                interface people already know. There is no equivalent on a
                laptop, so desktop gets getUserMedia instead. */}
            <input
              ref={cameraInput}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              capture="user"
              className="photopick__input"
              onChange={(e) => {
                setPending(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
            <button
              className="btn btn--ghost"
              disabled={uploading}
              onClick={() => (isMobile ? cameraInput.current?.click() : setCamera(true))}
            >
              Take photo
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Choose file'}
            </button>
            {photoUrl && !uploading && (
              <button className="photopick__remove" onClick={() => void dropPhoto()}>
                Remove
              </button>
            )}
          </div>
        </div>

        <div className="recform__grid">
          <label className="field recform__wide">
            <span>Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field recform__wide">
            <span>Title</span>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Account Executive"
            />
          </label>
          <label className="field">
            <span>Phone</span>
            <input
              className="input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+47 900 12 345"
            />
          </label>
          <label className="field">
            <span>Company</span>
            <input
              className="input"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Skida"
            />
          </label>
        </div>
        <p className="recform__note">
          Name and title sign the email suggestions on every lead. Your role and access are set by
          an admin under Team access.
        </p>
        {/* The design puts this in the modal footer. Here instead, next to the
            fields it depends on: opening the card before saving would show the
            old phone number, and the link is the moment you would notice. */}
        <button className="recform__link" onClick={() => setShowCard(true)}>
          View digital business card ↗
        </button>
        {error && <div className="recform__error">{error}</div>}
      </div>
      {camera && (
        <CameraCapture
          onCancel={() => setCamera(false)}
          onCaptured={(f) => {
            setCamera(false)
            setPending(f)
          }}
        />
      )}
      {pending && (
        <PhotoEditor
          file={pending}
          onCancel={() => setPending(null)}
          onDone={(blob) => void store(blob)}
        />
      )}
      {showCard && <BusinessCard onClose={() => setShowCard(false)} />}
    </Modal>
  )
}
